'use client';

import { cn } from '../../../../lib/utils';
import { cva } from 'class-variance-authority';
import { useEffect, useState } from 'react';

const requiredMarkSvg = () => {
  return (
    <svg
      width="9"
      height="8"
      viewBox="0 0 9 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.11048 7.13881L2.56091 4.66856L0 3.58074L0.453258 2.1983L3.1728 2.85552L3.4221 0H4.89518L5.14447 2.83286L7.86402 2.1983L8.31728 3.58074L5.75637 4.66856L7.22946 7.13881L6.05099 8L4.16997 5.8017L2.28895 8L1.11048 7.13881Z"
        className="fill-sys-cont-primary-default transition-all group-data-[custom-state=disabled]:fill-sys-cont-neutral-disabled-default"
      />
    </svg>
  );
};

const textareaVariants = cva(
  [
    'flex w-full min-h-fit outline-none px-sm py-xs border rounded-sm bg-comp-text-area-bg-default hover:bg-comp-text-area-bg-hover transition-all resize-none placeholder:transition-all text-comp-text-area-text-default caret-comp-text-area-caret placeholder:text-comp-text-area-placeholder',
  ],
  {
    variants: {
      state: {
        default:
          'border-comp-text-area-border-default focus-within:border-comp-text-area-border-focus ',
        disabled:
          'text-comp-text-area-text-disabled bg-comp-text-area-bg-disabled placeholder-comp-text-area-text-disabled cursor-not-allowed hover:bg-comp-text-area-bg-disabled',
        error: 'border-comp-text-area-border-error',
      },
      hasValue: {
        true: 'border-comp-text-area-border-focus group-data-[custom-state=error]:border-comp-text-area-border-error',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface TextAreaProps {
  /** 상태 */
  state?: 'default' | 'error' | 'disabled';
  /** 레이블 */
  label?: string;
  /** 레이블 표시 여부 */
  isShowLabel?: boolean;
  /** 필수 마크 표시 여부 */
  isShowRequiredMark?: boolean;
  /** 밑줄 표시 여부 */
  isShowBottomLine?: boolean;
  /** 최대 길이 표시 여부 */
  isMaxLength?: boolean;
  /** 최대 길이 */
  maxLength?: number | undefined;
  /** 헬퍼 텍스트 표시 여부 */
  isShowHelperText?: boolean;
  /** 헬퍼 텍스트 노드 */
  helperTextNode?: string | React.ReactNode;
  /** 문자 카운터 표시 여부 */
  isShowCharacterCounter?: boolean;
  /** 문자 카운터 노드 */
  characterNode?: React.ReactNode;
}

export default function TextArea({
  ...props
}: TextAreaProps & React.ComponentProps<'textarea'>) {
  const {
    state = 'default',
    isShowLabel = true,
    isShowRequiredMark,
    label,
    isMaxLength,
    maxLength,
    isShowHelperText,
    helperTextNode,
    isShowCharacterCounter,
    characterNode,
    isShowBottomLine,
    className,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(
    rest.defaultValue || rest.value || '',
  );
  const [charCount, setCharCount] = useState(
    typeof inputValue === 'string' ? inputValue.length : 0,
  );

  useEffect(() => {
    if (typeof rest.value === 'string') {
      // 방어 코드: maxLength가 설정되어 있으면 입력값을 최대 길이로 제한
      const limitedValue =
        isMaxLength && maxLength !== undefined
          ? rest.value.slice(0, maxLength)
          : rest.value;

      setInputValue(limitedValue);
      setCharCount(limitedValue.length);
    }
  }, [isMaxLength, maxLength, rest.value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // disabled 상태일 때는 이벤트 처리를 하지 않음
    if (state === 'disabled') return;
    let newValue = e.target.value;

    // 방어 코드: maxLength가 설정되어 있으면 입력값을 최대 길이로 제한
    if (isMaxLength && maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);

      // input 요소의 값을 직접 업데이트하여 사용자 인터페이스에도 반영
      e.target.value = newValue;
    }

    setInputValue(newValue);
    setCharCount(newValue.length);

    if (rest.onChange) {
      rest.onChange(e);
    }
  };

  const renderCharacterCounter = (isShowHelperText?: boolean) => {
    if (characterNode) {
      return characterNode;
    }

    if (maxLength) {
      return (
        <span
          className={cn(
            'text-sys-cont-neutral-light transition-all group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            !isShowHelperText ? 'w-full text-right' : '',
          )}
        >
          {charCount}/{maxLength}
        </span>
      );
    }

    return null;
  };

  return (
    <div
      data-custom-state={state}
      className={cn('group flex h-full w-full flex-col gap-5xs', className)}
    >
      {isShowLabel && label && (
        <div className="text-area-label text-caption-small">
          <label
            htmlFor={label}
            className={cn(
              'flex items-center gap-5xs text-sys-cont-neutral-light transition-all',
              'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            )}
          >
            {label}
            {isShowRequiredMark && requiredMarkSvg()}
          </label>
        </div>
      )}
      <div className="relative flex h-full w-full flex-1 text-body-medium">
        <textarea
          id={label}
          className={cn(
            'textarea-field',
            textareaVariants({ state, hasValue: !!inputValue }),
          )}
          onChange={handleInputChange}
          maxLength={isMaxLength ? maxLength : undefined}
          autoComplete={rest.autoComplete || 'off'}
          disabled={state === 'disabled'}
          {...rest}
        />
      </div>
      {isShowBottomLine && (
        <div
          className={cn(
            'input-text-field-bottom-line flex items-center justify-between px-xs transition-all',
            'text-caption-small',
          )}
        >
          {isShowHelperText && (
            <div className="text-sys-cont-neutral-light transition-all group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light group-data-[custom-state=error]:text-sys-cont-negative-default">
              {helperTextNode}
            </div>
          )}
          {isShowCharacterCounter && renderCharacterCounter(isShowHelperText)}
        </div>
      )}
    </div>
  );
}
